import { MessageSourceProps, withMessages } from 'react-message-source';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MedIndexDto } from 'ts-types/api.types';
import { editMedIndex, getMedIndex } from 'services/medIndexServices';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormContainer from 'components/layout/FormContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Button, Grid, Message } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import { required } from 'utils/validatorUtils';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';

interface Props extends MessageSourceProps {
}

interface State {
  pageDataLoaded: boolean,
  medIndexId?: number,
  medIndexData?: MedIndexDto,
  cancelTokenSource: CancelTokenSource,
  saveSuccess: boolean,
  errorMessage?: string
}

class MedIndexView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialValues: Partial<MedIndexDto> = {};

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      pageDataLoaded: false,
      medIndexId: undefined,
      saveSuccess: false,
      cancelTokenSource
    }
    this.fetchData();
  }

  fetchData = async () => {
    const {cancelTokenSource} = this.state;

    this.setState({
      pageDataLoaded: false
    })

    try {
      const medIndexData = await getMedIndex(cancelTokenSource);
      this.initialValues = {...medIndexData};

      this.setState({
        medIndexData: medIndexData
      });
    } catch (e) {
      this.handleError(e.response.data)
    } finally {
      this.setState({
        pageDataLoaded: true
      })
    }

  };

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage('error.general'))
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<MedIndexDto>) => {

    let saveValues: Partial<MedIndexDto> = {...values};

    const {cancelTokenSource} = this.state;

    const medIndexData = await editMedIndex(saveValues, cancelTokenSource)
        .catch((e: any) => this.handleError(e.response.data))

    if (medIndexData) {
      this.setState({
        medIndexData: medIndexData,
        saveSuccess: true
      });
    }

  };

  render(): React.ReactNode {

    if (!this.state.pageDataLoaded) {
      return <></>
    }

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {saveSuccess, errorMessage} = this.state;

    return (
        <PraxStyledSegment>
          <form onSubmit={handleSubmit}>
            <FormContainer>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {
                  saveSuccess &&
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message success onDismiss={() => this.setState({saveSuccess: false})}>
                        <p>
                          {getMessage("medIndexConfig.saveSuccess")}
                        </p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.wsMedUrl")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="wsMedUrl"
                        component={Input}
                        validate={required}
                        fluid
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.wsMedService")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="wsMedService"
                        component={Input}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.wsUsername")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="wsUsername"
                        component={Input}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.wsPassword")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="wsPassword"
                        component={Input}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.medAutomaticUpdate")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="medAutomaticUpdate"
                        component={CheckBox}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.medAutomaticUpdateDays")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="medAutomaticUpdateDays"
                        component={Input}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.medLastUpdate")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="medLastUpdate"
                        component={DatePicker}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={3}>
                    {getMessage("medIndexConfig.medLastInfo")} :
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Field
                        name="medLastInfo"
                        component={TextArea}
                        rows={2}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
              <FormButtonsRow>
                <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              </FormButtonsRow>
            </FormContainer>
          </form>
        </PraxStyledSegment>
    )
  }

}

export default withMessages(MedIndexView);

